import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Upload,
  Image as ImageIcon,
  Tag,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Heart,
  Building2,
  Store,
  Settings,
  Pencil,
  User,
  Plus,
  Mail,
  Filter,
  Clock,
  Calendar,
  CheckSquare,
  Square,
  X as XIcon,
  Tag as TagPriceIcon,
  ShoppingBag,
  ZoomIn as ZoomInIcon,
} from "lucide-react";
import { toast } from "sonner";
import api, { FILE_BASE, API } from "../lib/api";
import { isLoggedIn, getRole, getUser, getToken } from "../lib/auth";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PhotoTagLightbox from "../components/PhotoTagLightbox";

export default function PhotographerDashboard() {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(getUser());
  const [editingTag, setEditingTag] = useState(null);
  const [editingPrice, setEditingPrice] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [eventFilter, setEventFilter] = useState("all"); // "all" | event_id
  const [tagFilter, setTagFilter] = useState("all"); // "all" | "untagged" | "tagged"
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [orders, setOrders] = useState([]);
  const [tagLightbox, setTagLightbox] = useState(null); // {list: photos[], index: number} | null

  useEffect(() => {
    if (!isLoggedIn() || getRole() !== "photographer") {
      navigate("/photographer/login");
      return;
    }
    load();
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/photographers/me/orders").then((r) => setOrders(r.data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    if (params.get("onboarding") === "done") {
      api.get("/photographers/me/stripe-status").then((r) => {
        setUser((u) => ({ ...u, onboarding_status: r.data.onboarding_status }));
      }).catch(() => {});
    }
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      const r = await api.get("/photos/mine");
      setPhotos(r.data);
    } catch {
      // ignored
    } finally {
      setLoading(false);
    }
  };

  const onboard = async () => {
    try {
      const r = await api.post("/photographers/me/stripe-onboard");
      if (r.data.onboarding_url) {
        window.location.href = r.data.onboarding_url;
        return;
      }
      setUser({ ...user, ...r.data });
    } catch (err) {
      alert(err?.response?.data?.detail || "Kunde inte starta onboarding");
    }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Ta bort detta foto?")) return;
    await api.delete(`/photos/${id}`);
    load();
  };

  const saveTags = async (id) => {
    const nums = tagInput.split(/[,\s]+/).filter(Boolean);
    await api.patch(`/photos/${id}/tags`, { manual_numbers: nums });
    // Optimistic local update so the chip reflects the save before /photos/mine refetches
    setPhotos((prev) => prev.map((p) =>
      p.id === id ? { ...p, manual_numbers: nums, tags_overridden: true } : p,
    ));
    setEditingTag(null);
    setTagInput("");
    load();
  };

  const saveTagsFromLightbox = async (photoId, nums) => {
    await api.patch(`/photos/${photoId}/tags`, { manual_numbers: nums });
    // Optimistically update local list so the lightbox tags reflect the save
    setPhotos((prev) => prev.map((p) =>
      p.id === photoId
        ? { ...p, manual_numbers: nums, tags_overridden: nums.length > 0 || (p.detected_numbers || []).length > 0 }
        : p,
    ));
    load();
  };

  const openTagLightbox = (photo, group) => {
    const list = group && group.length ? group : [photo];
    const idx = list.findIndex((p) => p.id === photo.id);
    setTagLightbox({ list, index: idx === -1 ? 0 : idx });
  };

  const sourceThumbUrl = (id) => `${API}/photos/${id}/source-thumb?t=${encodeURIComponent(getToken() || "")}`;

  const resolveTags = (p) => {
    if (p.tags_overridden) return p.manual_numbers || [];
    return [...new Set([...(p.detected_numbers || []), ...(p.manual_numbers || [])])];
  };

  const savePrice = async (id) => {
    const price = parseFloat(priceInput);
    if (isNaN(price) || price < 10 || price > 999) {
      alert("Pris måste vara mellan 10 och 999 kr");
      return;
    }
    await api.patch(`/photos/${id}/price`, { price_sek: price });
    setEditingPrice(null);
    setPriceInput("");
    load();
  };

  const savePayout = async (data) => {
    try {
      const r = await api.patch("/photographers/me/payout", data);
      setUser(r.data);
      setPayoutOpen(false);
    } catch (err) {
      alert(err?.response?.data?.detail || "Kunde inte uppdatera");
    }
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Radera ${selected.size} foto${selected.size === 1 ? "" : "n"} permanent?`)) return;
    try {
      const r = await api.post("/photos/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${r.data.deleted} foton raderade`);
      exitSelectMode();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte radera");
    }
  };

  const bulkPrice = async () => {
    if (selected.size === 0) return;
    const input = window.prompt(`Sätt nytt pris för ${selected.size} foto${selected.size === 1 ? "" : "n"} (10–999 kr):`, "69");
    if (input === null) return;
    const price = parseFloat(input);
    if (isNaN(price) || price < 10 || price > 999) {
      toast.error("Pris måste vara mellan 10 och 999 kr");
      return;
    }
    try {
      const r = await api.post("/photos/bulk-price", { ids: Array.from(selected), price_sek: price });
      toast.success(`Pris uppdaterat på ${r.data.updated} foton`);
      exitSelectMode();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte uppdatera pris");
    }
  };

  const selectAllVisible = () => {
    setSelected(new Set(filtered.map((p) => p.id)));
  };

  // Compute event distribution from current photos
  const eventCounts = useMemo(() => {
    const map = {};
    for (const p of photos) {
      if (!map[p.event_id]) map[p.event_id] = { id: p.event_id, name: p.event_name, count: 0 };
      map[p.event_id].count += 1;
    }
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [photos]);

  // Filtered photos
  const filtered = useMemo(() => {
    let out = photos;
    if (eventFilter !== "all") out = out.filter((p) => p.event_id === eventFilter);
    if (tagFilter !== "all") {
      out = out.filter((p) => {
        const tags = p.tags_overridden
          ? (p.manual_numbers || [])
          : [...(p.detected_numbers || []), ...(p.manual_numbers || [])];
        return tagFilter === "untagged" ? tags.length === 0 : tags.length > 0;
      });
    }
    return out;
  }, [photos, eventFilter, tagFilter]);

  const untaggedCount = useMemo(
    () => photos.filter((p) => {
      const tags = p.tags_overridden
        ? (p.manual_numbers || [])
        : [...(p.detected_numbers || []), ...(p.manual_numbers || [])];
      return tags.length === 0;
    }).length,
    [photos],
  );

  // Split into "recent (24h)" + grouped-by-event
  const TWENTYFOUR_H_MS = 24 * 60 * 60 * 1000;
  const now = Date.now();
  const { recent, byEvent } = useMemo(() => {
    const rec = [];
    const groups = {};
    for (const p of filtered) {
      const ts = new Date(p.created_at).getTime();
      if (now - ts < TWENTYFOUR_H_MS) {
        rec.push(p);
      } else {
        const key = p.event_id;
        if (!groups[key]) groups[key] = { id: key, name: p.event_name, photos: [] };
        groups[key].photos.push(p);
      }
    }
    return { recent: rec, byEvent: Object.values(groups) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);

  const stats = {
    total: photos.length,
    ocrPending: photos.filter((p) => p.ocr_status === "pending").length,
    tagged: photos.filter(
      (p) => resolveTags(p).length > 0
    ).length,
  };

  const salesStats = useMemo(() => {
    const photosSold = orders.reduce((s, o) => s + (o.items?.length || 0), 0);
    const grossSek = orders.reduce(
      (s, o) => s + (o.items || []).reduce((a, it) => a + (it.price || 0), 0),
      0,
    );
    const earnedSek = orders.reduce((s, o) => s + (o.total_earned_sek || 0), 0);
    const since = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const last30Earned = orders
      .filter((o) => new Date(o.created_at).getTime() >= since)
      .reduce((s, o) => s + (o.total_earned_sek || 0), 0);
    return {
      orderCount: orders.length,
      photosSold,
      grossSek,
      earnedSek,
      last30Earned,
    };
  }, [orders]);

  const payoutIcons = { company: Building2, sponsorship: Heart, platform: Store };
  const payoutLabels = {
    company: `Företag${user?.company_name ? ` · ${user.company_name}` : ""}`,
    sponsorship: `Sponsring${user?.sponsorship_rider_name ? ` · ${user.sponsorship_rider_name}${user?.sponsorship_rider_bib ? ` #${user.sponsorship_rider_bib}` : ""}` : ""}`,
    platform: "Plattformen (100%)",
  };
  const PayoutIcon = payoutIcons[user?.payout_type || "sponsorship"];

  return (
    <div className="App">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            {user?.profile_image_url ? (
              <img
                src={`${FILE_BASE}${user.profile_image_url}`}
                alt={user?.name}
                className="w-14 h-14 rounded-full object-cover border border-white/10"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center border border-white/10">
                <User className="w-6 h-6 text-white/30" />
              </div>
            )}
            <div>
              <p className="label-overline mb-1">Fotograf · {user?.name}</p>
              <h1 className="font-display font-black uppercase text-white text-4xl sm:text-5xl">Dashboard</h1>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 self-start">
            <Link to="/photographer/profile" className="btn-secondary text-sm py-2 inline-flex items-center gap-2" data-testid="dashboard-profile-cta">
              <User className="w-4 h-4" /> Min profil
            </Link>
            <Link to="/photographer/orders" className="btn-secondary text-sm py-2 inline-flex items-center gap-2" data-testid="dashboard-orders-cta">
              <ShoppingBag className="w-4 h-4" /> Mina ordrar
            </Link>
            <Link to="/photographer/events/new" className="btn-secondary text-sm py-2 inline-flex items-center gap-2" data-testid="dashboard-create-event-cta">
              <Plus className="w-4 h-4" /> Nytt event
            </Link>
            <Link to="/photographer/upload" className="btn-primary text-sm py-2 inline-flex items-center gap-2" data-testid="dashboard-upload-cta">
              <Upload className="w-4 h-4" /> Ladda upp foton
            </Link>
          </div>
        </div>

        {/* Manage events (incl. ones without uploads yet) */}
        {events.length > 0 && (
          <div className="card-surface p-3 mb-4 flex flex-wrap items-center gap-3" data-testid="events-manage-row">
            <span className="label-overline">Hantera event</span>
            <select
              onChange={(e) => {
                if (e.target.value) navigate(`/photographer/events/${e.target.value}/manage`);
              }}
              defaultValue=""
              className="input-field text-sm py-1.5 max-w-xs flex-1"
              data-testid="events-manage-select"
              aria-label="Välj event att hantera"
            >
              <option value="" disabled>Välj event att hantera (startlista, priser)</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.name} {ev.date ? `· ${ev.date.slice(0, 10)}` : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Payout type display */}
        <div className="card-surface p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="payout-card">
          <PayoutIcon className="w-8 h-8 text-brand flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="label-overline">Utbetalningstyp</p>
            <p className="font-display font-bold uppercase text-white truncate">
              {payoutLabels[user?.payout_type || "company"]}
            </p>
          </div>
          <button onClick={() => setPayoutOpen(!payoutOpen)} className="btn-secondary text-sm py-2 flex items-center gap-1" data-testid="payout-edit-btn">
            <Settings className="w-4 h-4" /> Ändra
          </button>
        </div>

        {payoutOpen && <PayoutEditor user={user} onSave={savePayout} onCancel={() => setPayoutOpen(false)} />}

        {/* Onboarding warning */}
        {user?.payout_type === "company" && user?.onboarding_status !== "complete" && (
          <div className="bg-brand/10 border-l-4 border-brand p-4 mb-6 flex items-start gap-3" data-testid="onboarding-warning">
            <AlertCircle className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-display uppercase text-white font-bold">Aktivera utbetalningar</p>
              <p className="text-white/70 text-sm">
                Slutför Stripe Connect Express KYC för att få dina 90% direkt vid varje försäljning.
              </p>
            </div>
            <button onClick={onboard} className="btn-secondary text-sm py-2" data-testid="onboarding-btn">
              Anslut Stripe
            </button>
          </div>
        )}

        {/* Sales summary */}
        <div className="card-surface p-5 mb-4" data-testid="sales-summary">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="label-overline">Försäljning</p>
              <p className="font-display font-bold uppercase text-white text-lg">Hittills</p>
            </div>
            <Link
              to="/photographer/orders"
              className="text-brand hover:underline text-sm inline-flex items-center gap-1"
              data-testid="sales-summary-orders-link"
            >
              Visa alla ordrar →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="border border-white/5 bg-black/20 p-3">
              <p className="text-3xl font-display font-black text-white">{salesStats.grossSek.toFixed(0)} <span className="text-base text-white/50">kr</span></p>
              <p className="text-[11px] uppercase tracking-wider text-white/50 mt-1">Bruttoförsäljning</p>
            </div>
            <div className="border border-brand/30 bg-brand/5 p-3">
              <p className="text-3xl font-display font-black text-brand">{salesStats.earnedSek.toFixed(0)} <span className="text-base text-brand/60">kr</span></p>
              <p className="text-[11px] uppercase tracking-wider text-white/50 mt-1">
                {user?.payout_type === "platform" ? "Plattformen tar 100%" : "Din andel · 90%"}
              </p>
            </div>
            <div className="border border-white/5 bg-black/20 p-3">
              <p className="text-3xl font-display font-black text-white">{salesStats.photosSold}</p>
              <p className="text-[11px] uppercase tracking-wider text-white/50 mt-1">Sålda foton</p>
            </div>
            <div className="border border-white/5 bg-black/20 p-3">
              <p className="text-3xl font-display font-black text-white">{salesStats.last30Earned.toFixed(0)} <span className="text-base text-white/50">kr</span></p>
              <p className="text-[11px] uppercase tracking-wider text-white/50 mt-1">Senaste 30 dgr</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { l: "Totalt foton", v: stats.total, i: ImageIcon },
            { l: "Taggade", v: stats.tagged, i: Tag },
            { l: "Bearbetas", v: stats.ocrPending, i: Loader2 },
          ].map((s) => {
            const Icon = s.i;
            return (
              <div key={s.l} className="card-surface p-4">
                <Icon className="w-5 h-5 text-brand mb-2" />
                <p className="text-3xl font-display font-black text-white">{s.v}</p>
                <p className="text-xs uppercase tracking-wider text-white/50">{s.l}</p>
              </div>
            );
          })}
        </div>

        {/* Per-event quick stats */}
        {eventCounts.length > 0 && (
          <div className="card-surface p-4 mb-4" data-testid="event-distribution">
            <p className="label-overline mb-2">Foton per event</p>
            <div className="flex flex-wrap gap-2">
              {eventCounts.map((e) => (
                <button
                  key={e.id}
                  onClick={() => setEventFilter(e.id === eventFilter ? "all" : e.id)}
                  className={`text-xs px-3 py-1.5 border transition-colors ${
                    eventFilter === e.id
                      ? "bg-brand text-white border-brand"
                      : "border-white/10 text-white/70 hover:border-brand/50"
                  }`}
                  data-testid={`event-pill-${e.id}`}
                >
                  {e.name} <span className="text-brand-foreground/80">· {e.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filter row + Bulk actions */}
        {photos.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="dashboard-toolbar">
              <Filter className="w-4 h-4 text-white/50" />
              <select
                value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value)}
                className="input-field text-sm py-1.5 max-w-xs"
                data-testid="dashboard-event-filter"
              >
                <option value="all">Alla event ({photos.length})</option>
                {eventCounts.map((e) => (
                  <option key={e.id} value={e.id}>{e.name} ({e.count})</option>
                ))}
              </select>
              {eventFilter !== "all" && (
                <>
                  <Link
                    to={`/photographer/events/${eventFilter}/manage`}
                    className="text-xs text-brand hover:underline inline-flex items-center gap-1"
                    data-testid="dashboard-manage-event"
                    title="Hantera startlista för detta event"
                  >
                    Hantera startlista →
                  </Link>
                  <button
                    onClick={() => setEventFilter("all")}
                    className="text-xs text-white/50 hover:text-brand"
                    data-testid="dashboard-filter-clear"
                  >
                    Rensa filter
                  </button>
                </>
              )}
              {/* Tag-status filter pills */}
              <div className="flex gap-1" data-testid="dashboard-tag-filter">
                <button
                  onClick={() => setTagFilter("all")}
                  className={`text-xs px-3 py-1.5 border ${tagFilter === "all" ? "bg-brand text-white border-brand" : "border-white/10 text-white/70 hover:border-brand/50"}`}
                  data-testid="tag-filter-all"
                >
                  Alla
                </button>
                <button
                  onClick={() => setTagFilter("untagged")}
                  className={`text-xs px-3 py-1.5 border inline-flex items-center gap-1 ${tagFilter === "untagged" ? "bg-brand text-white border-brand" : "border-white/10 text-white/70 hover:border-brand/50"}`}
                  data-testid="tag-filter-untagged"
                >
                  Otaggade
                  {untaggedCount > 0 && (
                    <span className={`text-[10px] px-1 ${tagFilter === "untagged" ? "bg-white/20" : "bg-brand/20 text-brand"}`}>
                      {untaggedCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setTagFilter("tagged")}
                  className={`text-xs px-3 py-1.5 border ${tagFilter === "tagged" ? "bg-brand text-white border-brand" : "border-white/10 text-white/70 hover:border-brand/50"}`}
                  data-testid="tag-filter-tagged"
                >
                  Taggade
                </button>
              </div>
              <div className="flex-1" />
              {!selectMode ? (
                <button
                  onClick={() => setSelectMode(true)}
                  className="btn-secondary text-xs py-1.5 inline-flex items-center gap-1"
                  data-testid="bulk-select-mode"
                >
                  <CheckSquare className="w-3.5 h-3.5" /> Markera flera
                </button>
              ) : (
                <button
                  onClick={exitSelectMode}
                  className="btn-secondary text-xs py-1.5 inline-flex items-center gap-1"
                  data-testid="bulk-exit"
                >
                  <XIcon className="w-3.5 h-3.5" /> Avbryt
                </button>
              )}
            </div>

            {selectMode && (
              <div className="card-surface p-3 mb-4 flex flex-wrap items-center gap-3 border-l-4 border-brand" data-testid="bulk-action-bar">
                <span className="text-sm text-white">
                  <span className="text-brand font-bold">{selected.size}</span> markerade
                </span>
                <button
                  onClick={selectAllVisible}
                  className="text-xs text-white/70 hover:text-brand"
                  data-testid="bulk-select-all"
                >
                  Markera alla synliga ({filtered.length})
                </button>
                <button
                  onClick={() => setSelected(new Set())}
                  className="text-xs text-white/50 hover:text-brand"
                  disabled={selected.size === 0}
                >
                  Avmarkera
                </button>
                <div className="flex-1" />
                <button
                  onClick={bulkPrice}
                  disabled={selected.size === 0}
                  className="btn-secondary text-xs py-1.5 inline-flex items-center gap-1 disabled:opacity-40"
                  data-testid="bulk-price-btn"
                >
                  <TagPriceIcon className="w-3.5 h-3.5" /> Sätt pris
                </button>
                <button
                  onClick={bulkDelete}
                  disabled={selected.size === 0}
                  className="bg-red-700/40 hover:bg-red-700/60 text-red-300 text-xs py-1.5 px-3 inline-flex items-center gap-1 disabled:opacity-40 font-bold uppercase tracking-wider"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Radera
                </button>
              </div>
            )}
          </>
        )}

        {loading ? (
          <p className="text-white/50 text-center py-12">Laddar...</p>
        ) : photos.length === 0 ? (
          <div className="card-surface p-12 text-center" data-testid="dashboard-empty">
            <ImageIcon className="w-12 h-12 mx-auto text-white/20 mb-4" />
            <p className="font-display uppercase text-2xl text-white mb-2">Inga foton än</p>
            <p className="text-white/60 mb-4">Börja ladda upp dina event-bilder.</p>
            <Link to="/photographer/upload" className="btn-primary inline-flex">Ladda upp foton</Link>
          </div>
        ) : (
          <div className="space-y-8">
            {recent.length > 0 && eventFilter === "all" && tagFilter === "all" && (
              <PhotoSection
                title="Senast uppladdat"
                subtitle="Inom de senaste 24 timmarna"
                Icon={Clock}
                photos={recent}
                editingTag={editingTag}
                editingPrice={editingPrice}
                tagInput={tagInput}
                priceInput={priceInput}
                setTagInput={setTagInput}
                setPriceInput={setPriceInput}
                setEditingTag={setEditingTag}
                setEditingPrice={setEditingPrice}
                saveTags={saveTags}
                savePrice={savePrice}
                deletePhoto={deletePhoto}
                resolveTags={resolveTags}
                sourceThumbUrl={sourceThumbUrl}
                selectMode={selectMode}
                selected={selected}
                toggleSelected={toggleSelected}
                openTagLightbox={openTagLightbox}
                testid="dashboard-recent"
              />
            )}

            {eventFilter !== "all" ? (
              <PhotoSection
                title={photos.find((p) => p.event_id === eventFilter)?.event_name || "Filter"}
                subtitle={`${filtered.length} foton${tagFilter !== "all" ? ` · ${tagFilter === "untagged" ? "otaggade" : "taggade"}` : ""}`}
                Icon={Calendar}
                photos={filtered}
                editingTag={editingTag}
                editingPrice={editingPrice}
                tagInput={tagInput}
                priceInput={priceInput}
                setTagInput={setTagInput}
                setPriceInput={setPriceInput}
                setEditingTag={setEditingTag}
                setEditingPrice={setEditingPrice}
                saveTags={saveTags}
                savePrice={savePrice}
                deletePhoto={deletePhoto}
                resolveTags={resolveTags}
                sourceThumbUrl={sourceThumbUrl}
                selectMode={selectMode}
                selected={selected}
                toggleSelected={toggleSelected}
                openTagLightbox={openTagLightbox}
                testid="dashboard-filtered"
              />
            ) : (
              byEvent
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                .map((g) => (
                  <PhotoSection
                    key={g.id}
                    title={g.name || "Okänt event"}
                    subtitle={`${g.photos.length} foton · äldre än 24h`}
                    Icon={Calendar}
                    photos={g.photos}
                    editingTag={editingTag}
                    editingPrice={editingPrice}
                    tagInput={tagInput}
                    priceInput={priceInput}
                    setTagInput={setTagInput}
                    setPriceInput={setPriceInput}
                    setEditingTag={setEditingTag}
                    setEditingPrice={setEditingPrice}
                    saveTags={saveTags}
                    savePrice={savePrice}
                    deletePhoto={deletePhoto}
                    resolveTags={resolveTags}
                    sourceThumbUrl={sourceThumbUrl}
                    selectMode={selectMode}
                    selected={selected}
                    toggleSelected={toggleSelected}
                    openTagLightbox={openTagLightbox}
                    testid={`dashboard-group-${g.id}`}
                  />
                ))
            )}
          </div>
        )}

        {/* Support card */}
        <div className="card-surface p-5 mt-12 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="support-card">
          <Mail className="w-6 h-6 text-brand flex-shrink-0" />
          <div className="flex-1">
            <p className="font-display font-bold uppercase text-white">Behöver du hjälp?</p>
            <p className="text-white/60 text-sm">Mejla oss så svarar vi inom 24 timmar.</p>
          </div>
          <a
            href="mailto:info@enduroskola.se"
            className="btn-secondary text-sm py-2"
            data-testid="support-mail-link"
          >
            info@enduroskola.se
          </a>
        </div>
      </div>
      {tagLightbox && (
        <PhotoTagLightbox
          photos={tagLightbox.list}
          index={tagLightbox.index}
          onClose={() => setTagLightbox(null)}
          onIndexChange={(i) => setTagLightbox((s) => ({ ...s, index: i }))}
          onSaveTags={saveTagsFromLightbox}
        />
      )}
      <Footer />
    </div>
  );
}

function PhotoSection({
  title,
  subtitle,
  Icon,
  photos,
  editingTag,
  editingPrice,
  tagInput,
  priceInput,
  setTagInput,
  setPriceInput,
  setEditingTag,
  setEditingPrice,
  saveTags,
  savePrice,
  deletePhoto,
  resolveTags,
  sourceThumbUrl,
  selectMode,
  selected,
  toggleSelected,
  openTagLightbox,
  testid,
}) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-brand" />
        <h2 className="font-display font-bold uppercase text-white text-lg">{title}</h2>
        <span className="text-white/40 text-sm">· {subtitle}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.map((p) => {
          const all = resolveTags(p);
          const isEditing = editingTag === p.id;
          const isSelected = selected?.has(p.id);
          const cardClass = `card-surface ${isSelected ? "ring-2 ring-brand" : ""} ${selectMode ? "cursor-pointer" : ""}`;
          const handleCardClick = selectMode ? () => toggleSelected(p.id) : undefined;
          const handleImageClick = (e) => {
            if (selectMode) return; // let card click handle it
            e.stopPropagation();
            openTagLightbox && openTagLightbox(p, photos);
          };
          return (
            <div
              key={p.id}
              className={cardClass}
              data-testid={`my-photo-${p.id}`}
              onClick={handleCardClick}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#1a1a1a]">
                <img
                  src={isEditing ? sourceThumbUrl(p.id) : `${FILE_BASE}${p.thumb_url}`}
                  alt=""
                  className={`w-full h-full object-cover ${!selectMode ? "cursor-zoom-in" : ""}`}
                  onClick={handleImageClick}
                  data-testid={`photo-image-${p.id}`}
                />
                {selectMode && (
                  <div
                    className={`absolute top-2 left-2 w-6 h-6 flex items-center justify-center border-2 ${
                      isSelected ? "bg-brand border-brand" : "bg-black/60 border-white/60"
                    }`}
                    data-testid={`select-checkbox-${p.id}`}
                  >
                    {isSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                  </div>
                )}
                {isEditing && (
                  <span className="absolute bottom-1 left-1 bg-black/70 text-white/80 text-[10px] uppercase tracking-wider px-1.5 py-0.5">
                    Originalvy (utan vattenstämpel)
                  </span>
                )}
                {!selectMode && (
                  <div className="absolute top-2 left-2">
                    {p.ocr_status === "pending" && (
                      <span className="bg-yellow-500/80 text-black text-xs font-bold uppercase px-2 py-1 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> AI...
                      </span>
                    )}
                    {p.ocr_status === "done" && (
                      <span className="bg-green-600/80 text-white text-xs font-bold uppercase px-2 py-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Klar
                      </span>
                    )}
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  {editingPrice === p.id ? (
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <input
                        autoFocus
                        type="number"
                        value={priceInput}
                        onChange={(e) => setPriceInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && savePrice(p.id)}
                        className="w-16 bg-black/80 text-white text-xs px-1 py-0.5 border border-brand"
                        data-testid={`price-input-${p.id}`}
                      />
                      <button onClick={() => savePrice(p.id)} className="bg-brand text-white text-xs px-2" data-testid={`price-save-${p.id}`}>✓</button>
                    </div>
                  ) : (
                    !selectMode && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingPrice(p.id); setPriceInput(String(p.price_sek)); }}
                        className="bg-black/70 text-brand text-xs font-bold font-display px-2 py-1 flex items-center gap-1 hover:bg-brand hover:text-white"
                        data-testid={`price-edit-${p.id}`}
                      >
                        <Pencil className="w-3 h-3" /> {p.price_sek?.toFixed(0)} kr
                      </button>
                    )
                  )}
                </div>
              </div>
              <div className="p-3">
                <p className="text-xs text-white/50 truncate">{p.event_name}</p>
                {editingTag === p.id ? (
                  <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      placeholder="143, 27, 89  ·  tomt = inga taggar"
                      className="input-field text-sm py-1.5"
                      data-testid={`tag-input-${p.id}`}
                      autoFocus
                    />
                    <p className="text-[10px] text-white/40 mt-1">
                      Komma- eller mellanslagsseparerade siffror. Spara tomt för att rensa alla taggar.
                    </p>
                    <div className="flex gap-1 mt-1">
                      <button onClick={() => saveTags(p.id)} className="btn-primary text-xs py-1 flex-1" data-testid={`tag-save-${p.id}`}>Spara</button>
                      <button onClick={() => { setEditingTag(null); setTagInput(""); }} className="btn-secondary text-xs py-1 px-2">×</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); if (!selectMode) { setEditingTag(p.id); setTagInput(all.join(", ")); } }}
                      className="flex flex-wrap gap-1 mt-2 min-h-[1.5rem] w-full text-left hover:opacity-80 transition-opacity"
                      data-testid={`tag-chips-${p.id}`}
                      title="Klicka för att redigera taggar snabbt"
                      type="button"
                    >
                      {all.length > 0 ? all.map((n) => (
                        <span key={n} className="bg-brand/20 text-brand text-xs font-bold px-1.5 py-0.5">#{n}</span>
                      )) : <span className="text-white/30 text-xs italic">Klicka för att tagga</span>}
                    </button>
                    {!selectMode && (
                      <div className="flex gap-1 mt-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingTag(p.id); setTagInput(all.join(", ")); }}
                          className="btn-secondary text-xs py-1 flex-1"
                          data-testid={`edit-tags-${p.id}`}
                          title="Snabb-tagga (utan att öppna bilden)"
                        >
                          <Tag className="w-3 h-3 inline mr-1" /> Tagga
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); openTagLightbox && openTagLightbox(p, photos); }}
                          className="btn-secondary text-xs py-1 px-2"
                          data-testid={`zoom-${p.id}`}
                          title="Öppna stor bild för att zooma & tagga"
                        >
                          <ZoomInIcon className="w-3 h-3" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); deletePhoto(p.id); }} className="btn-secondary text-xs py-1 px-2" data-testid={`delete-${p.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function PayoutEditor({ user, onSave, onCancel }) {
  const [type, setType] = useState(user?.payout_type || "sponsorship");
  const [companyName, setCompanyName] = useState(user?.company_name || "");
  const [org, setOrg] = useState(user?.org_number || "");
  const [address, setAddress] = useState(user?.address || "");
  const [riderName, setRiderName] = useState(user?.sponsorship_rider_name || "");
  const [riderBib, setRiderBib] = useState(user?.sponsorship_rider_bib || "");
  const [riderClub, setRiderClub] = useState(user?.sponsorship_rider_club || "");
  const [publishPublic, setPublishPublic] = useState(user?.sponsorship_publish_public || false);

  const hasOrg = org.trim().length > 0;
  useEffect(() => {
    if (!hasOrg && type === "company") setType("sponsorship");
  }, [hasOrg, type]);

  const save = () => {
    onSave({
      payout_type: type,
      company_name: companyName || null,
      org_number: org || null,
      address: address || null,
      sponsorship_rider_name: type === "sponsorship" ? riderName : null,
      sponsorship_rider_bib: type === "sponsorship" ? riderBib : null,
      sponsorship_rider_club: type === "sponsorship" ? riderClub : null,
      sponsorship_publish_public: type === "sponsorship" ? publishPublic : false,
    });
  };

  return (
    <div className="card-surface p-4 mb-6 space-y-3" data-testid="payout-editor">
      <p className="label-overline">Byt utbetalningstyp och företagsinfo</p>
      <div className="space-y-2">
        <input placeholder="Företagsnamn" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="input-field" data-testid="payout-company-input" />
        <input placeholder="Org-nummer" value={org} onChange={(e) => setOrg(e.target.value)} className="input-field" data-testid="payout-org-input" />
        <input placeholder="Adress" value={address} onChange={(e) => setAddress(e.target.value)} className="input-field" data-testid="payout-address-input" />
      </div>
      <select value={type} onChange={(e) => setType(e.target.value)} className="input-field" data-testid="payout-type-select">
        <option value="sponsorship">Sponsring (till en förare)</option>
        {hasOrg && <option value="company">Företag (intäkter till Stripe Connect)</option>}
        <option value="platform">Plattformen (100%)</option>
      </select>
      {type === "sponsorship" && (
        <>
          <div className="grid grid-cols-[2fr_1fr] gap-2">
            <input placeholder="Fullständigt namn på förare" value={riderName} onChange={(e) => setRiderName(e.target.value)} className="input-field" data-testid="payout-rider-name" />
            <input placeholder="Startnr" value={riderBib} onChange={(e) => setRiderBib(e.target.value)} className="input-field" data-testid="payout-rider-bib" />
          </div>
          <input placeholder="Klubb" value={riderClub} onChange={(e) => setRiderClub(e.target.value)} className="input-field" data-testid="payout-rider-club" />
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={publishPublic} onChange={(e) => setPublishPublic(e.target.checked)} className="mt-1 accent-brand" data-testid="payout-publish-public" />
            <span className="text-sm text-white/70">Publicera föraren publikt på enduroskola-sajten (annars anonymt)</span>
          </label>
        </>
      )}
      <div className="flex gap-2">
        <button onClick={save} className="btn-primary" data-testid="payout-save">Spara</button>
        <button onClick={onCancel} className="btn-secondary">Avbryt</button>
      </div>
    </div>
  );
}
